import { Button } from "@/components/ui/button"
import { ProviderDto } from "@/api/api-client"
import { cn } from "@/lib/utils"

interface ProviderFilterItemProps {
  provider: ProviderDto
  isSelected: boolean
  onToggle: (id: number) => void
}

/**
 * Impeccable Industrial Design:
 *
 * 1. Multi-Select Affordance: Replaced checkboxes with a technical "Plus" icon.
 *    The Plus signals an additive action (multi-select) without the "form-like"
 *    clutter of a checkbox.
 * 2. Visual Alignment: The icon occupies the exact 20px (w-5) slot used by
 *    CategoryFilterItem's expansion icons, ensuring a unified vertical axis.
 * 3. Industrial Typography: Uses the project's high-contrast label style:
 *    text-xs, font-black, and widest tracking.
 * 4. Selection Feedback: Inherits the sidebar's signature border-l-2 and
 *    bg-primary/10 active state for a cohesive "Dashboard" feel.
 */
export const ProviderFilterItem = ({ provider, isSelected, onToggle }: ProviderFilterItemProps) => {
  return (
    <div className="group flex items-center gap-2 py-1">
      {/* Multi-select Indicator Slot (Solid Square) */}
      <div className="flex w-5 shrink-0 items-center justify-center">
        <div
          className={cn(
            "h-2 w-2 transition-colors duration-300",
            isSelected
              ? "bg-primary"
              : "bg-muted-foreground/30 group-hover:bg-muted-foreground/50"
          )}
        />
      </div>

      <Button
        variant="ghost"
        size="sm"
        className={cn(
          "h-8 flex-1 justify-start rounded-none px-2 transition-all duration-300",
          "text-sm uppercase tracking-wider",
          isSelected
            ? "font-black text-foreground"
            : "font-bold text-muted-foreground hover:bg-primary/10 hover:text-foreground dark:hover:bg-primary/20"
        )}
        onClick={() => onToggle(provider.id)}
      >
        <span className="truncate">{provider.name}</span>
      </Button>
    </div>
  )
}

